import React from 'react'
import { useInView } from 'react-intersection-observer'
import { useTranslation } from 'react-i18next'
import { BookOpen, GraduationCap, Briefcase, Scale, Users, BookMarked, FlaskConical } from 'lucide-react'

const Academics = () => {
  const { t } = useTranslation()
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.1
  })

  const programs = [
    {
      icon: BookOpen,
      title: 'K.G. TO 12TH',
      subtitle: 'SCHOOL EDUCATION',
      description: 'Comprehensive K-12 education with GSEB curriculum',
      color: 'from-primary-light/20 to-primary-mid/20',
      iconColor: 'text-primary-dark',
      borderColor: 'border-primary-mid/30'
    },
    {
      icon: FlaskConical,
      title: 'B.SC.',
      subtitle: 'BACHELOR OF SCIENCE',
      description: 'Science programs with practical learning',
      color: 'from-primary-light/20 to-primary-mid/20',
      iconColor: 'text-primary-dark',
      borderColor: 'border-primary-mid/30'
    },
    {
      icon: Briefcase,
      title: 'B.B.A.',
      subtitle: 'BACHELOR OF BUSINESS ADMINISTRATION',
      description: 'Business management and leadership',
      color: 'from-primary-light/20 to-primary-mid/20',
      iconColor: 'text-primary-dark',
      borderColor: 'border-primary-mid/30'
    },
    {
      icon: BookMarked,
      title: 'B.COM.',
      subtitle: 'BACHELOR OF COMMERCE',
      description: 'Commerce and accounting programs',
      color: 'from-primary-light/20 to-primary-mid/20',
      iconColor: 'text-primary-dark',
      borderColor: 'border-primary-mid/30'
    },
    {
      icon: GraduationCap,
      title: 'B.C.A.',
      subtitle: 'BACHELOR OF COMPUTER APPLICATIONS',
      description: 'Computer science and applications',
      color: 'from-primary-light/20 to-primary-mid/20',
      iconColor: 'text-primary-dark',
      borderColor: 'border-primary-mid/30'
    },
    {
      icon: BookOpen,
      title: 'B.A.',
      subtitle: 'BACHELOR OF ARTS',
      description: 'Arts and humanities programs',
      color: 'from-primary-light/20 to-primary-mid/20',
      iconColor: 'text-primary-dark',
      borderColor: 'border-primary-mid/30'
    },
    {
      icon: Users,
      title: 'B.ED.',
      subtitle: 'BACHELOR OF EDUCATION',
      description: 'Teacher training and education',
      color: 'from-primary-light/20 to-primary-mid/20',
      iconColor: 'text-primary-dark',
      borderColor: 'border-primary-mid/30'
    },
    {
      icon: Scale,
      title: 'LAW',
      subtitle: 'BACHELOR OF LAW',
      description: 'Legal studies and practice',
      color: 'from-primary-light/20 to-primary-mid/20',
      iconColor: 'text-primary-dark',
      borderColor: 'border-primary-mid/30'
    },
    {
      icon: BookMarked,
      title: 'M.COM.',
      subtitle: 'MASTER OF COMMERCE',
      description: 'Advanced commerce education',
      color: 'from-primary-light/20 to-primary-mid/20',
      iconColor: 'text-primary-dark',
      borderColor: 'border-primary-mid/30'
    },
    {
      icon: GraduationCap,
      title: 'M.A.',
      subtitle: 'MASTER OF ARTS',
      description: 'Postgraduate arts programs',
      color: 'from-primary-light/20 to-primary-mid/20',
      iconColor: 'text-primary-dark',
      borderColor: 'border-primary-mid/30'
    }
  ]

  return (
    <section id="academics" className="py-10 sm:py-12 md:py-16 lg:py-20 xl:py-24 section-light" ref={ref}>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 xl:px-10">
        {/* Header */}
        <div className={`text-center mb-10 sm:mb-12 lg:mb-16 transition-all duration-1000 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold text-primary-dark mb-3 sm:mb-4 font-poppins">
            FACULTIES
          </h2>
          <div className="w-16 sm:w-20 lg:w-24 h-1 bg-gradient-to-r from-primary-dark to-primary-mid mx-auto mb-4 sm:mb-6"></div>
          <p className="text-sm sm:text-base md:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto px-2">
            K.G. to Postgraduate Education - Comprehensive programs across diverse disciplines
          </p>
        </div>

        {/* Programs Grid - Responsive for all devices */}
        <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 sm:gap-6 lg:gap-8">
          {programs.map((program, index) => {
            const Icon = program.icon
            return (
              <div
                key={index}
                className={`group transition-all duration-700 delay-${index * 100} ${
                  inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                }`}
              >
                <div className={`relative h-full bg-gradient-to-br ${program.color} rounded-xl sm:rounded-2xl border-2 ${program.borderColor} p-4 sm:p-5 lg:p-6 hover:shadow-2xl hover:scale-105 transition-all duration-300`}>
                  {/* Hexagon Icon Container */}
                  <div className="flex justify-center mb-4 sm:mb-5 lg:mb-6">
                    <div className={`relative w-16 h-16 sm:w-20 sm:h-20 lg:w-24 lg:h-24 flex items-center justify-center bg-white rounded-lg sm:rounded-xl shadow-lg border-2 ${program.borderColor} group-hover:rotate-6 transition-transform duration-300`}>
                      <Icon className={`w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 ${program.iconColor}`} strokeWidth={1.5} />
                    </div>
                  </div>

                  {/* Program Title */}
                  <div className="text-center">
                    <h3 className={`text-lg sm:text-xl lg:text-2xl font-bold ${program.iconColor} mb-1 sm:mb-2 font-poppins`}>
                      {program.title}
                    </h3>
                    <p className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2 sm:mb-3 line-clamp-2">
                      {program.subtitle}
                    </p>
                    <p className="text-xs sm:text-sm text-gray-600 hidden sm:block">
                      {program.description}
                    </p>
                  </div>

                  {/* Decorative Element */}
                  <div className="absolute top-2 sm:top-3 right-2 sm:right-3 w-6 h-6 sm:w-8 sm:h-8 bg-white/50 rounded-full blur-sm"></div>
                  <div className="absolute bottom-2 sm:bottom-3 left-2 sm:left-3 w-4 h-4 sm:w-6 sm:h-6 bg-white/30 rounded-full blur-sm"></div>
                </div>
              </div>
            )
          })}
        </div>

        {/* Bottom CTA */}
        <div className={`text-center mt-10 sm:mt-12 lg:mt-16 transition-all duration-1000 delay-500 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <p className="text-gray-600 mb-4 sm:mb-6 text-sm sm:text-base lg:text-lg px-2">
            Explore our comprehensive academic programs and meet our faculty
          </p>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-center px-4">
            <a
              href="/academic-programs"
              className="btn-primary inline-block px-6 sm:px-8 py-3 sm:py-4 text-sm sm:text-base lg:text-lg hover:scale-105 shadow-lg hover:shadow-xl w-full sm:w-auto text-center touch-target"
            >
              View All Programs
            </a>
            <a
              href="/staff-details"
              className="btn-secondary inline-flex items-center justify-center gap-2 px-6 sm:px-8 py-3 sm:py-4 text-sm sm:text-base lg:text-lg hover:scale-105 shadow-lg hover:shadow-xl w-full sm:w-auto touch-target"
            >
              <Users className="w-4 h-4 sm:w-5 sm:h-5" />
              Faculty & Staff
            </a>
          </div>
        </div>
      </div>
    </section>
  )
}

export default Academics
